import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProfile } from '@hooks/profile';
import { PlaybookLang } from '../config/types';

const LANG_STORAGE_PREFIX = 'host_playbook_lang_';

const readStoredLang = (key: string): PlaybookLang | null => {
  try {
    const saved = window.localStorage.getItem(key);
    return saved === 'vi' || saved === 'en' ? saved : null;
  } catch {
    return null;
  }
};

/**
 * Guide language of the logged-in Host.
 * 1. The last VI/EN choice made in the panel, stored per account.
 * 2. Otherwise the interface language: Tiếng Việt -> VI, anything else -> EN.
 */
export const usePlaybookLang = (): [PlaybookLang, (next: PlaybookLang) => void] => {
  const { i18n } = useTranslation();
  const { data } = useProfile();
  const accountId = data?.data?.id;
  const storageKey = accountId ? `${LANG_STORAGE_PREFIX}${accountId}` : '';

  const [chosen, setChosen] = useState<PlaybookLang | null>(null);

  useEffect(() => {
    setChosen(storageKey ? readStoredLang(storageKey) : null);
  }, [storageKey]);

  const changeLang = useCallback(
    (next: PlaybookLang) => {
      setChosen(next);
      if (!storageKey) return;
      try {
        window.localStorage.setItem(storageKey, next);
      } catch {
        // storage unavailable: the choice only lasts for this session
      }
    },
    [storageKey],
  );

  const uiLang: PlaybookLang = i18n.language?.startsWith('vi') ? 'vi' : 'en';
  return [chosen ?? uiLang, changeLang];
};
